import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import type { FactorKostick, FactorKostickResultado, PuntajesKostick } from './types.js';

// Nombres descriptivos — sección 1.1 de papi_kostick_master_logic_v2.md.
// Solo nombres de factor; la matriz pregunta→factor nunca sale del backend.
const NOMBRE_FACTOR: Record<FactorKostick, string> = {
  G: 'Desempeñar una intensa actividad',
  L: 'Actividad de liderazgo',
  I: 'Facilidad para tomar decisiones',
  T: 'Tipo siempre activo',
  V: 'Tipo vigoroso',
  S: 'Extensión social',
  R: 'Tipo teórico',
  D: 'Interés en trabajo con detalles',
  C: 'Tipo organizado',
  E: 'Tipo emocionalmente restringido',
  N: 'Necesidad de terminar una tarea',
  A: 'Necesidad de logro',
  P: 'Necesidad de controlar a otros',
  X: 'Necesidad de ser notado',
  B: 'Necesidad de pertenecer al grupo',
  O: 'Necesidad de acercamiento y afecto',
  Z: 'Necesidad de cambio',
  K: 'Estado defensivo',
  F: 'Necesidad de apoyo del supervisor',
  W: 'Necesidad de reglas y supervisión',
};

const PUNTAJE_MAX_FACTOR = 9;

function nivelBadge(level: FactorKostickResultado['level']) {
  const map = {
    ALTO:     { cls: 'badge-info',    label: 'Alto' },
    PROMEDIO: { cls: 'badge-neutral', label: 'Promedio' },
    BAJO:     { cls: 'badge-warning', label: 'Bajo' },
  } as const;
  const { cls, label } = map[level];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function KostickRadar({ factors }: { factors: FactorKostickResultado[] }) {
  const data = factors.map((f) => ({ factor: f.code, Puntaje: f.score }));

  return (
    <ResponsiveContainer width="100%" height={340}>
      <RadarChart data={data}>
        <PolarGrid stroke="#e2e8f0" />
        <PolarAngleAxis dataKey="factor" tick={{ fontSize: 12, fontWeight: 700 }} />
        <PolarRadiusAxis angle={90} domain={[0, PUNTAJE_MAX_FACTOR]} tickCount={4} tick={{ fontSize: 10 }} />
        <Tooltip
          contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }}
          labelFormatter={(code: FactorKostick) => `${code} — ${NOMBRE_FACTOR[code] ?? code}`}
        />
        <Radar name="Puntaje" dataKey="Puntaje" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.35} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

function KostickTabla({ titulo, factors }: { titulo: string; factors: FactorKostickResultado[] }) {
  return (
    <div className="table-scroll" style={{ marginTop: 16 }}>
      <table className="main-table" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th>{titulo}</th>
            <th>Puntaje</th>
            <th>Nivel</th>
            <th>Aspecto positivo</th>
            <th>Aspecto negativo</th>
          </tr>
        </thead>
        <tbody>
          {factors.map((f) => (
            <tr key={f.code}>
              <td style={{ fontWeight: 600, minWidth: 160 }}>
                {f.code} — {NOMBRE_FACTOR[f.code] ?? f.code}
              </td>
              <td style={{ fontWeight: 600 }}>{f.score} / {PUNTAJE_MAX_FACTOR}</td>
              <td>{nivelBadge(f.level)}</td>
              <td style={{ whiteSpace: 'normal', minWidth: 220 }}>{f.interpretation.positive}</td>
              <td style={{ whiteSpace: 'normal', minWidth: 220 }}>{f.interpretation.negative}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function KostickResultado({ puntajes }: { puntajes: PuntajesKostick }) {
  const roles = puntajes.factors.filter((f) => f.category === 'ROL');
  const necesidades = puntajes.factors.filter((f) => f.category === 'NECESIDAD');

  return (
    <>
      <KostickRadar factors={puntajes.factors} />
      <KostickTabla titulo="Roles" factors={roles} />
      <KostickTabla titulo="Necesidades" factors={necesidades} />
    </>
  );
}
